import classNames from "classnames";
import Placeholder from "../../assets/noimageplaceholder.svg";
import { ImageNode, NodeStates } from "../../build/types";
import { useAspectRatio } from "../../utils/hooks";
import { BuilderComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./style.scss";
import { useState, useEffect } from "react";
import { useInteractionStates } from "../../hooks/useInteractionStates";

const getProxiedUrl = (originalUrl: string, proxyUrl?: string): string | null => {
    if (!proxyUrl || !originalUrl) return null;
    return `${proxyUrl}?url=${encodeURIComponent(originalUrl)}`;
};

const getUserOpacity = (style?: React.CSSProperties): number => {
    if (style?.opacity === undefined || style?.opacity === null) return 1;
    return typeof style.opacity === "number" ? style.opacity : parseFloat(String(style.opacity)) || 1;
};

const BuilderImage: React.FC<BuilderComponentProps> = ({
    node,
    actionCallbacks,
    children,
    dynamicData,
    builderProps,
    disabled,
    renderAdditionalProps
}: BuilderComponentProps) => {
    const [imageLoaded, setLoaded] = useState(false);
    const [currentSrc, setCurrentSrc] = useState<string | null>(null);
    const [triedProxy, setTriedProxy] = useState(false);
    const iNode = node as ImageNode;
    const { id, attr, stateManager, image } = iNode;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const proxyUrl = renderAdditionalProps?.proxy;
    const originalSrc = image?.url || dynamicData || Placeholder;
    const imageSrc = currentSrc || originalSrc;

    useEffect(() => {
        setLoaded(false);
        setCurrentSrc(null);
        setTriedProxy(false);
    }, [originalSrc]);

    const aspectRatio = useAspectRatio(imageSrc);

    const imageClasses = classNames("builder-image", {
        [`${attr?.className}`]: !!attr?.className,
        ["placeholder-image"]: !node.dynamicData && !image?.url,
        ["builder-hover"]: !!builderProps,
        ["obj-fit-cover"]: image?.style?.objectFit === "cover"
    });

    const handleLoaded = () => {
        setLoaded(true);
    };

    const handleError = () => {
        if (!triedProxy && proxyUrl) {
            const proxiedUrl = getProxiedUrl(originalSrc, proxyUrl);
            if (proxiedUrl) {
                setTriedProxy(true);
                setCurrentSrc(proxiedUrl);
                return;
            }
        }

        setCurrentSrc(Placeholder);
        setLoaded(true);
    };

    if (builderProps) {
        const { isContainer, nodeState, extraChild, selectedMenu, selectCallback, outlineCallback, onDragStart, onDragEnd } =
            builderProps;

        return (
            <div
                {...attr}
                className={imageClasses}
                style={getCompoundStyle({
                    node,
                    isContainer,
                    otherStyle: { aspectRatio },
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {!extraChild && (
                    <>
                        <img
                            key={imageSrc}
                            draggable={"false"}
                            src={imageSrc}
                            alt=""
                            style={getCompoundStyle({
                                otherStyle: {
                                    ...image?.style,
                                    opacity: imageLoaded ? getUserOpacity(image?.style) : 0,
                                    visibility: imageLoaded ? "visible" : "hidden"
                                },
                                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "image_style")
                            })}
                            onLoad={handleLoaded}
                            onError={handleError}
                            loading="lazy"
                        />
                    </>
                )}
                {selectedMenu}
                {extraChild}
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node, otherStyle: { aspectRatio } }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const imageStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: {
                ...image?.style,
                opacity: imageLoaded ? getUserOpacity(image?.style) : 0,
                visibility: imageLoaded ? "visible" : "hidden"
            },
            stateManager,
            paths: ["image_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div id={id} {...attr} {...actionCallbacks} className={imageClasses} ref={setElement} style={wrapperStyle}>
                {children}

                <img
                    key={imageSrc}
                    className="state-image"
                    src={imageSrc}
                    style={imageStyle}
                    onLoad={handleLoaded}
                    onError={handleError}
                    loading="lazy"
                />
                <div className="image-overlay" />
            </div>
        );
    }
};

export default BuilderImage;
